import React, { useState, useEffect } from 'react';
import { checkExistingTables } from '@/utils/checkDatabase';
import { Database, CheckCircle, XCircle, RefreshCw } from 'lucide-react';

export const DatabaseCheck: React.FC = () => {
  const [checking, setChecking] = useState(false);
  const [results, setResults] = useState<{
    existingTables: string[];
    missingTables: string[];
  } | null>(null);

  const runCheck = async () => {
    setChecking(true);
    try {
      const result = await checkExistingTables();
      setResults(result);
    } catch (error) {
      console.error('Error checking tables:', error);
    } finally {
      setChecking(false);
    }
  };

  useEffect(() => {
    runCheck();
  }, []);

  return (
    <div className="min-h-screen bg-zinc-900 text-white p-6">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-3xl font-bold mb-6 flex items-center gap-3">
          <Database className="w-8 h-8 text-blue-400" />
          Database Tables Check
        </h1>

        <button
          onClick={runCheck}
          disabled={checking}
          className="mb-6 px-6 py-2 bg-blue-500 rounded-lg hover:bg-blue-600 disabled:opacity-50 flex items-center gap-2"
        >
          <RefreshCw className={`w-5 h-5 ${checking ? 'animate-spin' : ''}`} />
          {checking ? 'Checking...' : 'Check Again'}
        </button>

        {results && (
          <div className="grid md:grid-cols-2 gap-6">
            {/* Existing Tables */}
            <div className="bg-zinc-800 rounded-xl p-6">
              <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                <CheckCircle className="w-6 h-6 text-green-400" />
                Existing Tables ({results.existingTables.length})
              </h2>
              <div className="space-y-2">
                {results.existingTables.map(table => (
                  <div key={table} className="flex items-center gap-2 p-2 bg-green-900/20 rounded-lg">
                    <CheckCircle className="w-4 h-4 text-green-400" />
                    <span className="text-green-300">{table}</span>
                  </div>
                ))}
                {results.existingTables.length === 0 && (
                  <p className="text-gray-400">No tables found</p>
                )}
              </div>
            </div>

            {/* Missing Tables */}
            <div className="bg-zinc-800 rounded-xl p-6">
              <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                <XCircle className="w-6 h-6 text-red-400" />
                Missing Tables ({results.missingTables.length})
              </h2>
              <div className="space-y-2">
                {results.missingTables.map(table => (
                  <div key={table} className="flex items-center gap-2 p-2 bg-red-900/20 rounded-lg">
                    <XCircle className="w-4 h-4 text-red-400" />
                    <span className="text-red-300">{table}</span>
                  </div>
                ))}
                {results.missingTables.length === 0 && (
                  <p className="text-gray-400">All tables exist!</p>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DatabaseCheck;